import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
	createFileRoute,
	useNavigate,
	useParams,
} from "@tanstack/react-router";
import {
	Building2,
	Check,
	ImageIcon,
	Loader2,
	Mail,
	Trash2,
	Upload,
	UserMinus,
	Users,
} from "lucide-react";
import {
	useCallback,
	useEffect,
	useId,
	useMemo,
	useRef,
	useState,
} from "react";
import { toast } from "sonner";
import { BrandButton } from "@/components/brand/brand-button";
import {
	BrandCard,
	BrandCardContent,
	BrandCardDescription,
	BrandCardHeader,
	BrandCardTitle,
} from "@/components/brand/brand-card";
import { BrandInput, BrandInputGroup } from "@/components/brand/brand-input";
import { useDashboardContext } from "@/components/dashboard/DashboardContext";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { DashboardPageHeader } from "@/components/dashboard/DashboardPageHeader";
import { useProDialog } from "@/components/ProDialog";
import {
	ConfirmDeleteDialog,
	ConfirmDeleteDialogConfirmation,
	ConfirmDeleteDialogContent,
	ConfirmDeleteDialogFooter,
	ConfirmDeleteDialogHeader,
} from "@/components/ui/confirm-delete-dialog";
import { useSession } from "@/hooks/auth-hooks";
import { useSubscription } from "@/hooks/use-subscription";
import { CONTACT_EMAIL } from "@/lib/constants";
import { getPlanCapabilities } from "@/lib/plans";
import {
	normalizeWorkspaceImageUrl,
	workspaceImageUrlFromStorageKey,
} from "@/lib/workspace-image-url";

export const Route = createFileRoute(
	"/dashboard/workspaces/$workspaceSlug/settings",
)({
	component: WorkspaceSettingsPage,
});

const pageShellClass =
	"mx-auto grid w-full max-w-none gap-8 px-4 py-8 sm:px-6 lg:grid-cols-[240px_minmax(0,1fr)] lg:px-8 lg:py-10 xl:max-w-[1800px]";
const sectionCardClass = "bg-card shadow-none";
const sectionHeaderClass =
	"mb-5 flex flex-col gap-2 border-b border-border pb-5 sm:flex-row sm:items-start sm:justify-between";
const sectionIconClass =
	"flex size-9 shrink-0 items-center justify-center rounded-md border border-border bg-background text-muted-foreground";
const sectionTitleClass = "text-base font-semibold tracking-normal";
const sectionDescriptionClass = "text-sm leading-6 text-muted-foreground";
const formClass = "grid gap-4";

interface Member {
	id: string;
	role: string;
	userId: string;
	userName?: string;
	userEmail?: string;
	userImage?: string;
	joinedAt: string;
}

interface Invitation {
	id: string;
	email: string;
	role: string;
	createdAt: string;
	expiresAt: string;
	inviterName?: string;
}

function WorkspaceSettingsPage() {
	const workspaceNameId = useId();
	const { workspaceSlug } = useParams({
		from: "/dashboard/workspaces/$workspaceSlug/settings",
	});
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { user } = useSession();
	const { tier, isPaid } = useSubscription();
	const capabilities = getPlanCapabilities(tier);
	const { openProDialog } = useProDialog();
	const { workspace, workspaces, switchWorkspace, isWorkspacesLoading } =
		useDashboardContext();

	const [inviteEmail, setInviteEmail] = useState("");
	const [inviteLoading, setInviteLoading] = useState(false);
	const [inviteError, setInviteError] = useState<string | null>(null);
	const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);

	const [renameName, setRenameName] = useState("");
	const [renameLoading, setRenameLoading] = useState(false);
	const [renameError, setRenameError] = useState<string | null>(null);
	const [renameSuccess, setRenameSuccess] = useState(false);

	const [imageLoading, setImageLoading] = useState(false);
	const [imageError, setImageError] = useState<string | null>(null);
	const imageInputRef = useRef<HTMLInputElement | null>(null);

	const [deleteConfirmName, setDeleteConfirmName] = useState("");
	const [deleteLoading, setDeleteLoading] = useState(false);
	const [deleteError, setDeleteError] = useState<string | null>(null);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

	const [leaveLoading, setLeaveLoading] = useState(false);

	const targetWorkspace = useMemo(
		() => workspaces.find((item) => item.slug === workspaceSlug) ?? null,
		[workspaceSlug, workspaces],
	);

	// Sync active workspace in the background -- don't block rendering
	useEffect(() => {
		if (isWorkspacesLoading || !targetWorkspace) return;
		if (workspace?.id === targetWorkspace.id) return;

		void switchWorkspace(targetWorkspace.id).catch(() => {
			// silent -- workspace data is already available via targetWorkspace
		});
	}, [isWorkspacesLoading, switchWorkspace, targetWorkspace, workspace?.id]);

	useEffect(() => {
		if (!targetWorkspace) return;
		setRenameName(targetWorkspace.name);
		setRenameError(null);
		setRenameSuccess(false);
	}, [targetWorkspace]);

	const { data, isLoading } = useQuery({
		queryKey: ["workspace-members"],
		queryFn: async () => {
			const res = await fetch("/api/workspaces/members", {
				credentials: "include",
			});
			if (!res.ok) throw new Error("Failed to load members");
			return res.json() as Promise<{
				members: Member[];
				invitations: Invitation[];
			}>;
		},
		enabled: !!user && !!targetWorkspace,
		refetchInterval: 15_000,
	});

	const members = data?.members ?? [];
	const invitations = data?.invitations ?? [];
	const currentMember = members.find((member) => member.userId === user?.id);
	const isOwner = currentMember?.role === "owner";
	const seatCount = members.length + invitations.length;

	const handleInvite = useCallback(
		async (e: React.FormEvent) => {
			e.preventDefault();
			if (!inviteEmail.trim()) return;

			setInviteLoading(true);
			setInviteError(null);
			setInviteSuccess(null);

			try {
				const res = await fetch("/api/workspaces/invitations", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					credentials: "include",
					body: JSON.stringify({ email: inviteEmail.trim().toLowerCase() }),
				});
				const result = await res.json();

				if (!res.ok) {
					setInviteError(result.error || "Failed to send invitation");
					return;
				}

				setInviteSuccess(`Invitation sent to ${inviteEmail}`);
				setInviteEmail("");
				queryClient.invalidateQueries({ queryKey: ["workspace-members"] });
			} catch {
				setInviteError("Failed to send invitation");
			} finally {
				setInviteLoading(false);
			}
		},
		[inviteEmail, queryClient],
	);

	const handleRemoveMember = useCallback(
		async (memberId: string, name: string) => {
			if (!confirm(`Remove ${name} from this workspace?`)) return;

			try {
				const res = await fetch("/api/workspaces/members", {
					method: "DELETE",
					headers: { "Content-Type": "application/json" },
					credentials: "include",
					body: JSON.stringify({ memberId }),
				});
				const result = await res.json();
				if (!res.ok) {
					toast.error(result.error || "Failed to remove member");
					return;
				}
				queryClient.invalidateQueries({ queryKey: ["workspace-members"] });
			} catch {
				toast.error("Failed to remove member");
			}
		},
		[queryClient],
	);

	const handleCancelInvitation = useCallback(
		async (invitationId: string) => {
			try {
				const res = await fetch("/api/workspaces/invitations", {
					method: "DELETE",
					headers: { "Content-Type": "application/json" },
					credentials: "include",
					body: JSON.stringify({ invitationId }),
				});
				const result = await res.json();
				if (!res.ok) {
					toast.error(result.error || "Failed to cancel invitation");
					return;
				}
				queryClient.invalidateQueries({ queryKey: ["workspace-members"] });
			} catch {
				toast.error("Failed to cancel invitation");
			}
		},
		[queryClient],
	);

	const handleRename = useCallback(
		async (e: React.FormEvent) => {
			e.preventDefault();
			if (!targetWorkspace || !renameName.trim()) return;

			setRenameLoading(true);
			setRenameError(null);
			setRenameSuccess(false);

			try {
				const res = await fetch("/api/workspaces", {
					method: "PATCH",
					headers: { "Content-Type": "application/json" },
					credentials: "include",
					body: JSON.stringify({
						workspaceId: targetWorkspace.id,
						name: renameName.trim(),
					}),
				});
				const result = await res.json();
				if (!res.ok) {
					setRenameError(result.error || "Failed to rename workspace");
					return;
				}
				setRenameSuccess(true);
				await queryClient.invalidateQueries({ queryKey: ["workspaces"] });
				await queryClient.invalidateQueries({
					queryKey: ["workspace-members"],
				});
				navigate({
					to: "/dashboard/workspaces/$workspaceSlug/settings",
					params: { workspaceSlug: result.workspace.slug },
					replace: true,
				});
			} catch {
				setRenameError("Failed to rename workspace");
			} finally {
				setRenameLoading(false);
			}
		},
		[navigate, queryClient, renameName, targetWorkspace],
	);

	const handleImageUpload = useCallback(
		async (file: File) => {
			if (!targetWorkspace) return;
			if (!file.type.startsWith("image/")) {
				setImageError("Please choose an image file");
				return;
			}

			setImageLoading(true);
			setImageError(null);

			try {
				const formData = new FormData();
				formData.set("file", file);
				const uploadRes = await fetch("/api/social/upload-media", {
					method: "POST",
					credentials: "include",
					body: formData,
				});
				const uploadResult = (await uploadRes.json().catch(() => null)) as {
					sourceUrl?: string;
					storageKey?: string;
					error?: string;
				} | null;
				if (!uploadRes.ok || !uploadResult?.sourceUrl) {
					setImageError(uploadResult?.error ?? "Failed to upload image");
					return;
				}

				const patchRes = await fetch("/api/workspaces", {
					method: "PATCH",
					credentials: "include",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						workspaceId: targetWorkspace.id,
						imageUrl: uploadResult.storageKey
							? workspaceImageUrlFromStorageKey(uploadResult.storageKey)
							: uploadResult.sourceUrl,
					}),
				});
				const patchResult = await patchRes.json().catch(() => null);
				if (!patchRes.ok) {
					setImageError(
						(patchResult as { error?: string } | null)?.error ??
							"Failed to save image",
					);
					return;
				}
				toast.success("Workspace image updated");
				await queryClient.invalidateQueries({ queryKey: ["workspaces"] });
			} catch {
				setImageError("Failed to upload image");
			} finally {
				setImageLoading(false);
				if (imageInputRef.current) imageInputRef.current.value = "";
			}
		},
		[queryClient, targetWorkspace],
	);

	const handleImageRemove = useCallback(async () => {
		if (!targetWorkspace) return;

		setImageLoading(true);
		setImageError(null);
		try {
			const res = await fetch("/api/workspaces", {
				method: "PATCH",
				credentials: "include",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					workspaceId: targetWorkspace.id,
					imageUrl: null,
				}),
			});
			const result = await res.json().catch(() => null);
			if (!res.ok) {
				setImageError(
					(result as { error?: string } | null)?.error ??
						"Failed to remove image",
				);
				return;
			}
			toast.success("Workspace image removed");
			await queryClient.invalidateQueries({ queryKey: ["workspaces"] });
		} catch {
			setImageError("Failed to remove image");
		} finally {
			setImageLoading(false);
		}
	}, [queryClient, targetWorkspace]);

	const handleDelete = useCallback(async () => {
		if (!targetWorkspace) return;

		setDeleteLoading(true);
		setDeleteError(null);

		try {
			const res = await fetch("/api/workspaces", {
				method: "DELETE",
				headers: { "Content-Type": "application/json" },
				credentials: "include",
				body: JSON.stringify({ workspaceId: targetWorkspace.id }),
			});
			const result = await res.json();
			if (!res.ok) {
				setDeleteError(result.error || "Failed to delete workspace");
				return;
			}
			await queryClient.invalidateQueries({ queryKey: ["workspaces"] });
			setDeleteDialogOpen(false);
			setDeleteConfirmName("");
			navigate({ to: "/dashboard" });
		} catch {
			setDeleteError("Failed to delete workspace");
		} finally {
			setDeleteLoading(false);
		}
	}, [navigate, queryClient, targetWorkspace]);

	const handleLeave = useCallback(async () => {
		if (!confirm("Are you sure you want to leave this workspace?")) return;

		setLeaveLoading(true);
		try {
			const res = await fetch("/api/workspaces/members", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				credentials: "include",
				body: JSON.stringify({ action: "leave" }),
			});
			const result = await res.json();
			if (!res.ok) {
				toast.error(result.error || "Failed to leave workspace");
				return;
			}
			await queryClient.invalidateQueries({ queryKey: ["workspaces"] });
			navigate({ to: "/dashboard" });
		} catch {
			toast.error("Failed to leave workspace");
		} finally {
			setLeaveLoading(false);
		}
	}, [navigate, queryClient]);

	if (!targetWorkspace && !isWorkspacesLoading) {
		return (
			<DashboardLayout>
				<div className={pageShellClass}>
					<div className="lg:col-start-2 flex min-h-[50vh] items-center justify-center">
						<p className="text-sm text-muted-foreground">
							Workspace not found.
						</p>
					</div>
				</div>
			</DashboardLayout>
		);
	}

	if (!targetWorkspace) {
		return (
			<DashboardLayout>
				<div className={pageShellClass}>
					<div className="lg:col-start-2 flex min-h-[50vh] items-center justify-center">
						<Loader2 className="mx-auto size-5 animate-spin text-muted-foreground" />
					</div>
				</div>
			</DashboardLayout>
		);
	}

	return (
		<DashboardLayout>
			<div className={pageShellClass}>
				<div className="lg:sticky lg:top-24 lg:self-start">
					<DashboardPageHeader
						title="Settings"
						description={`Manage ${targetWorkspace.name}, its members, and destructive actions.`}
					/>
				</div>

				<div className="space-y-4">
					{/* General */}
					<BrandCard
						variant="default"
						padding="lg"
						className={sectionCardClass}
					>
						<BrandCardHeader className={sectionHeaderClass}>
							<div className="flex items-start gap-3">
								<div className={sectionIconClass}>
									<Building2 className="size-4" />
								</div>
								<div>
									<BrandCardTitle className={sectionTitleClass}>
										Workspace name
									</BrandCardTitle>
									<BrandCardDescription className={sectionDescriptionClass}>
										Used to identify this workspace across the app.
									</BrandCardDescription>
								</div>
							</div>
						</BrandCardHeader>
						<BrandCardContent>
							{isOwner ? (
								<form onSubmit={handleRename} className={formClass}>
									<div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
										<BrandInputGroup
											label="Display name"
											className="[&_label]:text-sm [&_p]:text-sm"
										>
											<BrandInput
												id={workspaceNameId}
												type="text"
												value={renameName}
												onChange={(event) => {
													setRenameName(event.target.value);
													setRenameError(null);
													setRenameSuccess(false);
												}}
												maxLength={80}
												className="h-9 text-sm file:text-sm"
												icon={<Building2 className="size-4" />}
											/>
										</BrandInputGroup>
										<BrandButton
											type="submit"
											size="sm"
											className="self-end"
											disabled={
												renameLoading ||
												!renameName.trim() ||
												renameName.trim() === targetWorkspace.name
											}
										>
											{renameLoading ? (
												<Loader2 className="size-3.5 animate-spin" />
											) : (
												<>
													<Check className="size-4" />
													Save
												</>
											)}
										</BrandButton>
									</div>
									{renameError && (
										<p className="mt-2 text-sm text-destructive">
											{renameError}
										</p>
									)}
									{renameSuccess && (
										<p className="mt-2 text-sm text-emerald-600">
											Workspace renamed.
										</p>
									)}
								</form>
							) : (
								<p className="text-sm text-muted-foreground">
									Only workspace owners can rename this workspace.
								</p>
							)}
						</BrandCardContent>
					</BrandCard>

					{/* Image */}
					<BrandCard
						variant="default"
						padding="lg"
						className={sectionCardClass}
					>
						<BrandCardHeader className={sectionHeaderClass}>
							<div className="flex items-start gap-3">
								<div className={sectionIconClass}>
									<ImageIcon className="size-4" />
								</div>
								<div>
									<BrandCardTitle className={sectionTitleClass}>
										Workspace image
									</BrandCardTitle>
									<BrandCardDescription className={sectionDescriptionClass}>
										Shown in the sidebar and workspace switcher.
									</BrandCardDescription>
								</div>
							</div>
						</BrandCardHeader>
						<BrandCardContent>
							{isOwner ? (
								<div className="flex flex-wrap items-center gap-4">
									<div className="flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-muted text-base font-semibold text-muted-foreground">
										{targetWorkspace.imageUrl ? (
											<img
												src={
													normalizeWorkspaceImageUrl(
														targetWorkspace.imageUrl,
													) ?? undefined
												}
												alt=""
												className="size-full object-cover"
											/>
										) : (
											targetWorkspace.name.slice(0, 1).toUpperCase()
										)}
									</div>
									<div className="flex flex-wrap items-center gap-2">
										<input
											ref={imageInputRef}
											type="file"
											accept="image/*"
											className="hidden"
											onChange={(event) => {
												const file = event.target.files?.[0];
												if (file) void handleImageUpload(file);
											}}
										/>
										<BrandButton
											type="button"
											size="sm"
											onClick={() => imageInputRef.current?.click()}
											disabled={imageLoading}
										>
											{imageLoading ? (
												<Loader2 className="size-3.5 animate-spin" />
											) : (
												<>
													<Upload className="size-4" />
													{targetWorkspace.imageUrl ? "Replace" : "Upload"}
												</>
											)}
										</BrandButton>
										{targetWorkspace.imageUrl && (
											<BrandButton
												type="button"
												size="sm"
												onClick={handleImageRemove}
												disabled={imageLoading}
											>
												Remove
											</BrandButton>
										)}
									</div>
									{imageError && (
										<p className="basis-full text-sm text-destructive">
											{imageError}
										</p>
									)}
								</div>
							) : (
								<p className="text-sm text-muted-foreground">
									Only workspace owners can change the workspace image.
								</p>
							)}
						</BrandCardContent>
					</BrandCard>

					{/* Members */}
					<BrandCard
						variant="default"
						padding="lg"
						className={sectionCardClass}
					>
						<BrandCardHeader className={sectionHeaderClass}>
							<div className="flex items-start gap-3">
								<div className={sectionIconClass}>
									<Users className="size-4" />
								</div>
								<div>
									<BrandCardTitle className={sectionTitleClass}>
										Members
									</BrandCardTitle>
									<BrandCardDescription className={sectionDescriptionClass}>
										{seatCount} of {capabilities.maxTeamMembers} seats used.
									</BrandCardDescription>
								</div>
							</div>
						</BrandCardHeader>
						<BrandCardContent>
							{isOwner && (
								<form onSubmit={handleInvite} className={formClass}>
									<div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
										<BrandInputGroup
											label="Invite by email"
											className="[&_label]:text-sm [&_p]:text-sm"
										>
											<BrandInput
												type="email"
												value={inviteEmail}
												onChange={(event) => {
													setInviteEmail(event.target.value);
													setInviteError(null);
													setInviteSuccess(null);
												}}
												placeholder="maya@company.com"
												className="h-9 text-sm file:text-sm"
												icon={<Mail className="size-4" />}
												required
											/>
										</BrandInputGroup>
										<BrandButton
											type="submit"
											size="sm"
											className="self-end"
											disabled={
												inviteLoading ||
												seatCount >= capabilities.maxTeamMembers
											}
										>
											{inviteLoading ? (
												<Loader2 className="size-3.5 animate-spin" />
											) : (
												<>
													<Mail className="size-4" />
													Invite
												</>
											)}
										</BrandButton>
									</div>
									{inviteError && (
										<p className="mt-2 text-sm text-destructive">
											{inviteError}
										</p>
									)}
									{inviteSuccess && (
										<p className="mt-2 text-sm text-emerald-600">
											{inviteSuccess}
										</p>
									)}
									{seatCount >= capabilities.maxTeamMembers && (
										<p className="mt-2 text-sm text-muted-foreground">
											{tier === "max" ? (
												<>
													All seats are in use. Need more? Email{" "}
													<a
														href={`mailto:${CONTACT_EMAIL}`}
														className="text-primary hover:underline"
													>
														{CONTACT_EMAIL}
													</a>
													.
												</>
											) : (
												<>
													All seats are in use.{" "}
													<button
														type="button"
														onClick={() => {
															openProDialog({ tier: isPaid ? "max" : "pro" });
														}}
														className="text-primary hover:underline"
													>
														Upgrade
													</button>{" "}
													to add more.
												</>
											)}
										</p>
									)}
								</form>
							)}

							<div className="mt-5 overflow-hidden rounded-lg border border-border bg-background">
								{isLoading ? (
									<div className="px-4 py-3 text-sm text-muted-foreground">
										Loading...
									</div>
								) : members.length === 0 ? (
									<div className="px-4 py-3 text-sm text-muted-foreground">
										No members found.
									</div>
								) : (
									<ul className="divide-y divide-border">
										{members.map((member) => (
											<li
												key={member.id}
												className="flex items-center justify-between gap-3 px-4 py-3"
											>
												<div className="flex min-w-0 items-center gap-3">
													<div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-xs font-medium uppercase text-muted-foreground">
														{(member.userName || member.userEmail || "?").slice(
															0,
															1,
														)}
													</div>
													<div className="min-w-0">
														<p className="truncate text-sm text-foreground">
															{member.userName || member.userEmail}
															{member.userId === user?.id && (
																<span className="ml-1 text-muted-foreground">
																	(you)
																</span>
															)}
														</p>
														{member.userEmail && member.userName && (
															<p className="text-xs text-muted-foreground">
																{member.userEmail}
															</p>
														)}
													</div>
												</div>
												<div className="flex items-center gap-2">
													<span className="rounded-md bg-muted px-2 py-1 text-xs capitalize text-muted-foreground">
														{member.role}
													</span>
													{isOwner &&
														member.role !== "owner" &&
														member.userId !== user?.id && (
															<button
																type="button"
																onClick={() =>
																	handleRemoveMember(
																		member.id,
																		member.userName ||
																			member.userEmail ||
																			"this member",
																	)
																}
																className="inline-flex size-8 items-center justify-center rounded-md text-destructive transition-colors hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
																aria-label={`Remove ${
																	member.userName ||
																	member.userEmail ||
																	"member"
																}`}
															>
																<UserMinus className="size-4" />
															</button>
														)}
												</div>
											</li>
										))}
									</ul>
								)}
							</div>

							{invitations.length > 0 && (
								<div className="mt-4">
									<p className="mb-2 text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
										Pending invitations
									</p>
									<div className="overflow-hidden rounded-lg border border-border bg-background">
										<ul className="divide-y divide-border">
											{invitations.map((invitation) => (
												<li
													key={invitation.id}
													className="flex items-center justify-between gap-3 px-4 py-3"
												>
													<div className="min-w-0">
														<p className="truncate text-sm text-foreground">
															{invitation.email}
														</p>
														<p className="text-xs text-muted-foreground">
															Invited{" "}
															{new Date(
																invitation.createdAt,
															).toLocaleDateString()}
														</p>
													</div>
													{isOwner && (
														<button
															type="button"
															onClick={() =>
																handleCancelInvitation(invitation.id)
															}
															className="text-xs font-medium text-destructive transition-colors hover:text-destructive/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
														>
															Cancel
														</button>
													)}
												</li>
											))}
										</ul>
									</div>
								</div>
							)}
						</BrandCardContent>
					</BrandCard>

					{/* Leave workspace (non-owners) */}
					{!isOwner && currentMember && (
						<BrandCard
							variant="default"
							padding="lg"
							className={sectionCardClass}
						>
							<BrandCardHeader className={sectionHeaderClass}>
								<div className="flex items-start gap-3">
									<div className={sectionIconClass}>
										<UserMinus className="size-4" />
									</div>
									<div>
										<BrandCardTitle className={sectionTitleClass}>
											Leave workspace
										</BrandCardTitle>
										<BrandCardDescription className={sectionDescriptionClass}>
											You will lose access to this workspace and its content.
										</BrandCardDescription>
									</div>
								</div>
							</BrandCardHeader>
							<BrandCardContent>
								<BrandButton
									type="button"
									size="sm"
									onClick={handleLeave}
									disabled={leaveLoading}
								>
									{leaveLoading ? "Leaving..." : "Leave workspace"}
								</BrandButton>
							</BrandCardContent>
						</BrandCard>
					)}

					{/* Danger zone (owners) */}
					{isOwner && (
						<BrandCard
							variant="default"
							padding="lg"
							className="border-destructive/25 bg-card shadow-none"
						>
							<BrandCardHeader className={sectionHeaderClass}>
								<div className="flex items-start gap-3">
									<div className="flex size-9 shrink-0 items-center justify-center rounded-md border border-destructive/25 bg-background text-destructive">
										<Trash2 className="size-4" />
									</div>
									<div>
										<BrandCardTitle className="text-base font-semibold tracking-normal text-destructive">
											Delete workspace
										</BrandCardTitle>
										<BrandCardDescription className={sectionDescriptionClass}>
											Permanently remove this workspace, its members, content,
											and connections.
										</BrandCardDescription>
									</div>
								</div>
							</BrandCardHeader>
							<BrandCardContent>
								<BrandButton
									type="button"
									size="sm"
									onClick={() => setDeleteDialogOpen(true)}
									disabled={workspaces.length <= 1}
								>
									<Trash2 className="size-4" />
									Delete workspace
								</BrandButton>
								{workspaces.length <= 1 && (
									<p className="mt-2 text-sm text-muted-foreground">
										You cannot delete your only workspace.
									</p>
								)}
								{deleteError && (
									<p className="mt-2 text-sm text-destructive">{deleteError}</p>
								)}
							</BrandCardContent>
						</BrandCard>
					)}

					<ConfirmDeleteDialog
						open={deleteDialogOpen}
						onOpenChange={(open) => {
							setDeleteDialogOpen(open);
							if (!open) {
								setDeleteConfirmName("");
								setDeleteError(null);
							}
						}}
					>
						<ConfirmDeleteDialogContent>
							<ConfirmDeleteDialogHeader
								title="Delete workspace"
								description="This will permanently delete this workspace, all its members, content, and connections. This action cannot be undone."
							/>
							<ConfirmDeleteDialogConfirmation
								value={deleteConfirmName}
								onChange={setDeleteConfirmName}
								confirmText={targetWorkspace.name}
								showCopyButton
							/>
							{deleteError && (
								<p className="px-1 text-sm text-destructive">{deleteError}</p>
							)}
							<ConfirmDeleteDialogFooter
								onCancel={() => {
									setDeleteDialogOpen(false);
									setDeleteConfirmName("");
									setDeleteError(null);
								}}
								onConfirm={handleDelete}
								confirmDisabled={deleteConfirmName !== targetWorkspace.name}
								isLoading={deleteLoading}
								confirmLabel="Delete workspace"
							/>
						</ConfirmDeleteDialogContent>
					</ConfirmDeleteDialog>
				</div>
			</div>
		</DashboardLayout>
	);
}
